/**
 * Messaging Service
 * Phase 6: Communication & Engagement
 * Handles all messaging operations between buyers and sellers
 */

import { supabase } from '../lib/supabase';
import { messagesAPI } from './api';

export const messagingService = {
  // ============================================
  // Permission Checks
  // ============================================

  /**
   * Check if a seller can send a message to a buyer
   * @param {string} sellerId - Seller UUID
   * @param {string} buyerId - Buyer UUID
   * @returns {Promise<{allowed: boolean, reason: string, matchScore: number}>}
   */
  async canSendMessage(sellerId, buyerId) {
    try {
      const { data, error } = await supabase.rpc('can_send_message', {
        p_seller_id: sellerId,
        p_buyer_id: buyerId
      });

      if (error) throw error;

      if (data && data.length > 0) {
        return {
          allowed: data[0].allowed,
          reason: data[0].reason,
          matchScore: data[0].match_score
        };
      }

      // Default fallback
      return { allowed: true, reason: 'Allowed', matchScore: 0 };
    } catch (err) {
      console.error('Error checking message permission:', err);
      // Fallback to allow (don't block if check fails)
      return { allowed: true, reason: 'Permission check unavailable', matchScore: 0 };
    }
  },

  // ============================================
  // Conversations
  // ============================================

  /**
   * Get or create a conversation between seller and buyer
   * @param {string} sellerId - Seller UUID
   * @param {string} buyerId - Buyer UUID
   * @param {string} [subject] - Conversation subject
   * @param {string} [assetId] - Optional asset ID
   * @returns {Promise<string>} - Conversation ID
   */
  async getOrCreateConversation(sellerId, buyerId, subject = null, assetId = null) {
    const { data, error } = await supabase.rpc('get_or_create_conversation', {
      p_seller_id: sellerId,
      p_buyer_id: buyerId,
      p_subject: subject,
      p_asset_id: assetId
    });

    if (error) throw error;
    // RPC returns UUID directly, wrap in object for consistency
    return typeof data === 'string' ? { id: data } : data;
  },

  /**
   * Get conversations for a user (seller or buyer)
   * @param {string} userId - User's seller or buyer ID
   * @param {string} userType - 'seller' or 'buyer'
   * @param {Object} [options] - Query options
   * @returns {Promise<Array>}
   */
  async getConversations(userId, userType, options = {}) {
    const { status = 'active', limit = 50, offset = 0 } = options;

    let query = supabase
      .from('v_conversation_summary')
      .select('*')
      .eq('status', status)
      .order('last_message_at', { ascending: false, nullsFirst: false })
      .range(offset, offset + limit - 1);

    if (userType === 'seller') {
      query = query.eq('seller_id', userId);
    } else {
      query = query.eq('buyer_id', userId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  /**
   * Get a single conversation by ID
   * @param {string} conversationId - Conversation UUID
   * @returns {Promise<Object>}
   */
  async getConversation(conversationId) {
    // Ensure conversationId is a string (handle object with id property)
    const id = typeof conversationId === 'object' ? conversationId?.id : conversationId;

    if (!id) {
      console.warn('[Messaging] getConversation called with invalid id:', conversationId);
      return null;
    }

    const { data, error } = await supabase
      .from('v_conversation_summary')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Archive a conversation
   * @param {string} conversationId - Conversation UUID
   */
  async archiveConversation(conversationId) {
    // Ensure conversationId is a string (handle object with id property)
    const id = typeof conversationId === 'object' ? conversationId?.id : conversationId;

    if (!id) {
      console.warn('[Messaging] archiveConversation called with invalid id:', conversationId);
      return;
    }

    const { error } = await supabase
      .from('conversations')
      .update({ status: 'archived', updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * Get unread conversation count
   * @param {string} userId - User's seller or buyer ID
   * @param {string} userType - 'seller' or 'buyer'
   * @returns {Promise<number>}
   */
  async getUnreadCount(userId, userType) {
    const column = userType === 'seller' ? 'unread_for_seller' : 'unread_for_buyer';
    const idColumn = userType === 'seller' ? 'seller_id' : 'buyer_id';

    const { data, error } = await supabase
      .from('v_conversation_summary')
      .select(column)
      .eq(idColumn, userId)
      .eq('status', 'active');

    if (error) throw error;

    return data?.reduce((sum, row) => sum + (row[column] || 0), 0) || 0;
  },

  // ============================================
  // Messages
  // ============================================

  /**
   * Send a message
   * @param {Object} messageData - Message data
   * @returns {Promise<string>} - Message ID
   */
  async sendMessage({ conversationId, senderId, senderType, subject, body, attachments = [], skipNotification = false }) {
    console.log('[Messaging] Sending message...');

    // Direct insert instead of RPC (bypasses schema cache issues)
    // Select full message data for immediate UI update
    const { data: messageData, error: insertError } = await supabase
      .from('messages')
      .insert({
        conversation_id: conversationId,
        sender_id: senderId,
        sender_type: senderType,
        subject: subject || null,
        body: body,
        attachments: attachments.length > 0 ? attachments : []
      })
      .select('*')  // Select all fields for immediate display
      .single();

    if (insertError) {
      console.error('[Messaging] Insert message error:', insertError);
      throw insertError;
    }

    console.log('[Messaging] Message inserted successfully:', messageData?.id);

    // Update conversation
    const { error: updateError } = await supabase
      .from('conversations')
      .update({
        last_message_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', conversationId);

    if (updateError) {
      console.warn('[Messaging] Update conversation error:', updateError);
    }

    // Return full message object for immediate UI update
    const data = messageData;

    // Trigger email notification for reply messages (don't await)
    if (!skipNotification) {
      // Get conversation to find recipient
      this.getConversation(conversationId).then(conv => {
        if (conv) {
          const recipientType = senderType === 'seller' ? 'buyer' : 'seller';
          const recipientId = senderType === 'seller' ? conv.buyer_id : conv.seller_id;
          messagesAPI.notifyNewMessage({
            conversationId,
            recipientId,
            recipientType,
            messagePreview: body
          }).catch(err => console.warn('Failed to send email notification:', err));
        }
      }).catch(err => console.warn('Failed to get conversation for notification:', err));
    }

    return data;
  },

  /**
   * Send a new message (convenience method that creates conversation if needed)
   * @param {Object} params - Message parameters
   * @returns {Promise<{conversationId: string, messageId: string}>}
   */
  async sendNewMessage({ sellerId, buyerId, senderType, subject, body, attachments = [], assetId = null }) {
    // Get or create conversation
    const conversation = await this.getOrCreateConversation(sellerId, buyerId, subject, assetId);
    const conversationId = conversation?.id || conversation; // Handle both object and string returns

    // Trigger email notification (don't await - fire and forget)
    const recipientType = senderType === 'seller' ? 'buyer' : 'seller';
    const recipientId = senderType === 'seller' ? buyerId : sellerId;
    messagesAPI.notifyNewMessage({
      conversationId,
      recipientId,
      recipientType,
      messagePreview: body
    }).catch(err => console.warn('Failed to send email notification:', err));

    // Send message (skip notification since we already triggered it above)
    const messageId = await this.sendMessage({
      conversationId,
      senderId: senderType === 'seller' ? sellerId : buyerId,
      senderType,
      subject,
      body,
      attachments,
      skipNotification: true
    });

    return { conversationId, messageId };
  },

  /**
   * Get messages in a conversation
   * @param {string} conversationId - Conversation UUID
   * @param {Object} [options] - Query options
   * @returns {Promise<Array>}
   */
  async getMessages(conversationId, options = {}) {
    // Ensure conversationId is a string (handle object with id property)
    const id = typeof conversationId === 'object' ? conversationId?.id : conversationId;

    if (!id) {
      console.warn('[Messaging] getMessages called with invalid id:', conversationId);
      return [];
    }

    const { limit = 100, offset = 0, includeDeleted = false } = options;

    let query = supabase
      .from('messages')
      .select('*')
      .eq('conversation_id', id)
      .order('created_at', { ascending: true })
      .range(offset, offset + limit - 1);

    if (!includeDeleted) {
      query = query.eq('is_deleted', false);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  /**
   * Mark messages as read
   * @param {string} conversationId - Conversation UUID
   * @param {string} readerType - 'seller' or 'buyer'
   * @returns {Promise<number>} - Number of messages marked as read
   */
  async markAsRead(conversationId, readerType) {
    // Ensure conversationId is a string (handle object with id property)
    const id = typeof conversationId === 'object' ? conversationId?.id : conversationId;

    if (!id) {
      console.warn('[Messaging] markAsRead called with invalid id:', conversationId);
      return 0;
    }

    const { data, error } = await supabase.rpc('mark_messages_read', {
      p_conversation_id: id,
      p_reader_type: readerType
    });

    if (error) throw error;

    // Also update conversation's updated_at to trigger real-time subscriptions
    // This ensures the sidebar unread count updates when messages are read
    await supabase
      .from('conversations')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', id);

    return data || 0;
  },

  /**
   * Delete a message (soft delete)
   * @param {string} messageId - Message UUID
   */
  async deleteMessage(messageId) {
    const { error } = await supabase
      .from('messages')
      .update({
        is_deleted: true,
        deleted_at: new Date().toISOString()
      })
      .eq('id', messageId);

    if (error) throw error;
  },

  // ============================================
  // Templates
  // ============================================

  /**
   * Get message templates
   * @param {string} [category] - Optional category filter
   * @returns {Promise<Array>}
   */
  async getTemplates(category = null) {
    let query = supabase
      .from('message_templates')
      .select('*')
      .eq('is_active', true)
      .order('display_order');

    if (category) {
      query = query.eq('category', category);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  /**
   * Get a single template by ID
   * @param {string} templateId - Template UUID
   * @returns {Promise<Object>}
   */
  async getTemplate(templateId) {
    const { data, error } = await supabase
      .from('message_templates')
      .select('*')
      .eq('id', templateId)
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Fill a template with data
   * @param {string} templateId - Template UUID
   * @param {Object} data - Placeholder values
   * @returns {Promise<{subject: string, body: string}>}
   */
  async fillTemplate(templateId, data) {
    const template = await this.getTemplate(templateId);

    let subject = template.subject_template;
    let body = template.body_template;

    // Replace placeholders
    Object.entries(data).forEach(([key, value]) => {
      const placeholder = `{{${key}}}`;
      subject = subject.replace(new RegExp(placeholder, 'g'), value || '');
      body = body.replace(new RegExp(placeholder, 'g'), value || '');
    });

    // Update usage count
    await supabase
      .from('message_templates')
      .update({ usage_count: template.usage_count + 1 })
      .eq('id', templateId);

    return { subject, body };
  },

  // ============================================
  // Attachments
  // ============================================

  /**
   * Upload an attachment
   * @param {File} file - File to upload
   * @param {string} conversationId - Conversation UUID
   * @returns {Promise<Object>} - Attachment metadata
   */
  async uploadAttachment(file, conversationId) {
    const fileName = `${conversationId}/${Date.now()}_${file.name}`;

    const { data, error } = await supabase.storage
      .from('message-attachments')
      .upload(fileName, file);

    if (error) throw error;

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('message-attachments')
      .getPublicUrl(fileName);

    return {
      name: file.name,
      url: urlData.publicUrl,
      size: file.size,
      type: file.type
    };
  },

  /**
   * Delete an attachment
   * @param {string} path - File path in storage
   */
  async deleteAttachment(path) {
    const { error } = await supabase.storage
      .from('message-attachments')
      .remove([path]);

    if (error) throw error;
  },

  // ============================================
  // Permissions
  // ============================================

  /**
   * Get messaging permissions for a buyer
   * @param {string} buyerId - Buyer UUID
   * @returns {Promise<Object>}
   */
  async getPermissions(buyerId) {
    const { data, error } = await supabase
      .from('messaging_permissions')
      .select('*')
      .eq('buyer_id', buyerId)
      .single();

    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows

    // Return defaults if no permissions set
    return data || {
      allow_messages: true,
      require_minimum_match_score: 50,
      require_profile_view: true,
      auto_approve_messages: true,
      max_messages_per_seller_per_month: 3,
      block_suspicious_senders: true,
      blocked_sellers: []
    };
  },

  /**
   * Update messaging permissions
   * @param {string} buyerId - Buyer UUID
   * @param {Object} permissions - Permission settings
   */
  async updatePermissions(buyerId, permissions) {
    const { error } = await supabase
      .from('messaging_permissions')
      .upsert({
        buyer_id: buyerId,
        ...permissions,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'buyer_id'
      });

    if (error) throw error;
  },

  /**
   * Block a seller
   * @param {string} buyerId - Buyer UUID
   * @param {string} sellerId - Seller UUID to block
   */
  async blockSeller(buyerId, sellerId) {
    const permissions = await this.getPermissions(buyerId);
    const blockedSellers = [...(permissions.blocked_sellers || []), sellerId];

    await this.updatePermissions(buyerId, {
      blocked_sellers: [...new Set(blockedSellers)] // Remove duplicates
    });
  },

  /**
   * Unblock a seller
   * @param {string} buyerId - Buyer UUID
   * @param {string} sellerId - Seller UUID to unblock
   */
  async unblockSeller(buyerId, sellerId) {
    const permissions = await this.getPermissions(buyerId);
    const blockedSellers = (permissions.blocked_sellers || []).filter(id => id !== sellerId);

    await this.updatePermissions(buyerId, {
      blocked_sellers: blockedSellers
    });
  },

  // ============================================
  // Analytics
  // ============================================

  /**
   * Get messaging statistics for a seller
   * @param {string} sellerId - Seller UUID
   * @returns {Promise<Object>}
   */
  async getStats(sellerId) {
    // Try to get from view, fallback to calculating from conversations
    try {
      const { data, error } = await supabase
        .from('v_messaging_stats')
        .select('*')
        .eq('seller_id', sellerId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      if (data) return data;
    } catch (err) {
      console.warn('Stats view not available, calculating manually');
    }

    // Fallback: calculate basic stats from conversations
    const { data: conversations } = await supabase
      .from('conversations')
      .select('*')
      .eq('seller_id', sellerId);

    const { data: messages } = await supabase
      .from('messages')
      .select('*, conversations!inner(seller_id)')
      .eq('conversations.seller_id', sellerId);

    const sent = messages?.filter(m => m.sender_type === 'seller').length || 0;
    const read = messages?.filter(m => m.sender_type === 'seller' && m.is_read).length || 0;
    const replies = messages?.filter(m => m.sender_type === 'buyer').length || 0;

    return {
      totalSent: sent,
      totalRead: read,
      totalReplies: replies,
      meetingsBooked: 0,
      avgResponseTime: 0,
      readRate: sent > 0 ? Math.round((read / sent) * 100) : 0,
      replyRate: sent > 0 ? Math.round((replies / sent) * 100) : 0,
      activeConversations: conversations?.length || 0,
      responseTimeByDay: [
        { label: 'Mon', value: 0 },
        { label: 'Tue', value: 0 },
        { label: 'Wed', value: 0 },
        { label: 'Thu', value: 0 },
        { label: 'Fri', value: 0 },
        { label: 'Sat', value: 0 },
        { label: 'Sun', value: 0 }
      ],
      topMessages: []
    };
  },

  /**
   * Get conversation analytics
   * @param {string} conversationId - Conversation UUID
   * @returns {Promise<Array>}
   */
  async getConversationAnalytics(conversationId) {
    const { data, error } = await supabase
      .from('message_analytics')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('sent_at', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  // ============================================
  // Real-time Subscriptions
  // ============================================

  /**
   * Subscribe to new messages in a conversation
   * @param {string} conversationId - Conversation UUID
   * @param {Function} callback - Callback function for new messages
   * @returns {Object} - Subscription object
   */
  subscribeToConversation(conversationId, callback) {
    // Ensure conversationId is a string (handle object with id property)
    const id = typeof conversationId === 'object' ? conversationId?.id : conversationId;

    if (!id) {
      console.warn('[Messaging] subscribeToConversation called with invalid id:', conversationId);
      return null;
    }

    return supabase
      .channel(`messages-${id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `conversation_id=eq.${id}`
      }, (payload) => {
        callback(payload.new);
      })
      .subscribe();
  },

  /**
   * Subscribe to conversation list updates
   * @param {string} userId - User's seller or buyer ID
   * @param {string} userType - 'seller' or 'buyer'
   * @param {Function} callback - Callback function
   * @returns {Object} - Subscription object
   */
  subscribeToConversations(userId, userType, callback) {
    const column = userType === 'seller' ? 'seller_id' : 'buyer_id';

    return supabase
      .channel(`conversations-${userId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'conversations',
        filter: `${column}=eq.${userId}`
      }, (payload) => {
        callback(payload);
      })
      .subscribe();
  },

  /**
   * Unsubscribe from a channel
   * @param {Object} subscription - Subscription object
   */
  unsubscribe(subscription) {
    if (subscription) {
      supabase.removeChannel(subscription);
    }
  }
};

export default messagingService;
